/**
 * Read a font's real family, weight and style out of the file.
 *
 * ── Why not just parse the filename ──
 *
 * The filename is what someone's export dialog happened to produce:
 * `Canela-Rg_v3_FINAL.otf`, `GT-America-Standard-Medium-Italic-Trial.ttf`,
 * `webfont.woff`. Guessing from it is right often enough to be tempting and
 * wrong often enough to make the user re-type three fields.
 *
 * Every OpenType file already carries the authoritative answer in its `name`
 * table — the same values the OS shows in a font menu. Reading it is about a
 * hundred lines with no dependency, which is cheaper than shipping a font
 * library for one modal.
 *
 * ── What is supported ──
 *
 * Bare sfnt (`.ttf`, `.otf`) is read directly. `.woff` is the same tables with
 * a different header and per-table zlib compression, which `DecompressionStream`
 * handles. `.woff2` uses Brotli with a rebuilt table directory, which the
 * platform gives us no way to undo — those fall back to the filename guess.
 *
 * Anything unreadable returns null rather than throwing: a font we can't parse
 * must still be uploadable, just with the fields filled in by hand.
 */

export interface FontMetadata {
	family: string;
	/** "Bold", "Light" — never includes the slant. */
	weight: string;
	/** "Italic", or empty. */
	style: string;
}

/* ── name IDs, per the OpenType spec ── */
const NAME_FAMILY = 1;
const NAME_SUBFAMILY = 2;
/** Preferred family — set when the basic family was split to fit the old
 * four-styles-per-family limit ("Canela Text" + "Bold" rather than
 * "Canela Text Bold" + "Regular"). Preferred wins when present. */
const NAME_TYPOGRAPHIC_FAMILY = 16;
const NAME_TYPOGRAPHIC_SUBFAMILY = 17;

export async function readFontMetadata(file: File): Promise<FontMetadata | null> {
	try {
		const buffer = await file.arrayBuffer();
		const view = new DataView(buffer);
		if (view.byteLength < 12) return null;

		const tag = view.getUint32(0);
		/* 'wOF2' — Brotli, and the table directory is rebuilt rather than
		   copied, so there is nothing to seek to without a decoder. */
		if (tag === 0x774f4632) return null;

		const names =
			tag === 0x774f4646 /* 'wOFF' */
				? await readWoffNameTable(buffer, view)
				: readSfntNameTable(buffer, view);

		if (!names) return null;

		const family = names.get(NAME_TYPOGRAPHIC_FAMILY) || names.get(NAME_FAMILY) || '';
		const subfamily = names.get(NAME_TYPOGRAPHIC_SUBFAMILY) || names.get(NAME_SUBFAMILY) || '';
		if (!family) return null;

		return { family: family.trim(), ...splitSubfamily(subfamily) };
	} catch {
		return null;
	}
}

/**
 * "Bold Italic" → weight "Bold", style "Italic".
 *
 * The two travel together in one string and belong in separate fields, because
 * a face is not "Bold Italic weight" — it is bold, and slanted.
 */
function splitSubfamily(subfamily: string): { weight: string; style: string } {
	const slant = /italic|oblique/i.exec(subfamily);
	const weight = subfamily
		.replace(/italic|oblique/gi, '')
		.replace(/\s+/g, ' ')
		.trim();

	/* An upright face's subfamily is "Regular", which IS the weight rather than a
	   placeholder, so it is kept. A pure "Italic" leaves the weight empty, which
	   is also correct — the font said nothing about it. */
	return { weight, style: slant ? capitalize(slant[0]) : '' };
}

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

/** Locate a table in an sfnt directory and return its [offset, length]. */
function findTable(view: DataView, base: number, name: string): [number, number] | null {
	const numTables = view.getUint16(base + 4);
	const wanted = name.split('').reduce((acc, c) => (acc << 8) | c.charCodeAt(0), 0) >>> 0;

	for (let i = 0; i < numTables; i++) {
		const record = base + 12 + i * 16;
		if (record + 16 > view.byteLength) return null;
		if (view.getUint32(record) === wanted) {
			return [view.getUint32(record + 8), view.getUint32(record + 12)];
		}
	}
	return null;
}

function readSfntNameTable(buffer: ArrayBuffer, view: DataView): Map<number, string> | null {
	const table = findTable(view, 0, 'name');
	if (!table) return null;
	return parseNameTable(new DataView(buffer, table[0], table[1]));
}

/**
 * WOFF wraps the same tables in its own directory, each optionally deflated.
 *
 * Only the `name` table is decompressed — there is no reason to inflate an
 * entire font to read three strings off it.
 */
async function readWoffNameTable(
	buffer: ArrayBuffer,
	view: DataView
): Promise<Map<number, string> | null> {
	const numTables = view.getUint16(12);
	const wanted = 0x6e616d65; /* 'name' */

	for (let i = 0; i < numTables; i++) {
		const record = 44 + i * 20;
		if (record + 20 > view.byteLength) return null;
		if (view.getUint32(record) !== wanted) continue;

		const offset = view.getUint32(record + 4);
		const compLength = view.getUint32(record + 8);
		const origLength = view.getUint32(record + 12);
		const slice = buffer.slice(offset, offset + compLength);

		/* Equal lengths mean the table was stored uncompressed. */
		const raw = compLength === origLength ? slice : await inflate(slice);
		return raw ? parseNameTable(new DataView(raw)) : null;
	}
	return null;
}

async function inflate(data: ArrayBuffer): Promise<ArrayBuffer | null> {
	if (typeof DecompressionStream === 'undefined') return null;
	try {
		const stream = new Blob([data]).stream().pipeThrough(new DecompressionStream('deflate'));
		return await new Response(stream).arrayBuffer();
	} catch {
		return null;
	}
}

const WINDOWS_PLATFORM = 3;
const MAC_PLATFORM = 1;
const WINDOWS_EN_US = 0x0409;
const MAC_ENGLISH = 0;

/**
 * How much a given record is worth, so the best wins regardless of table order.
 *
 * A font carries the same name many times over: per platform, per encoding, and
 * per LANGUAGE. Georgia ships its subfamily as "Bold Italic", "Gras Italique",
 * "Fett Kursiv" and more, and the English one is not first — preferring only the
 * Windows platform picks whichever translation happens to lead, which is how
 * "Gras Italique" ended up in the weight box.
 *
 * English is what the rest of this UI is in and what the family/weight fields
 * are compared against, so it is ranked first explicitly.
 */
function rankRecord(platformId: number, languageId: number): number {
	if (platformId === WINDOWS_PLATFORM && languageId === WINDOWS_EN_US) return 4;
	if (platformId === MAC_PLATFORM && languageId === MAC_ENGLISH) return 3;
	/* Windows records are UTF-16 and present in essentially every modern font,
	   so a non-English one still beats a Mac record in an 8-bit encoding. */
	if (platformId === WINDOWS_PLATFORM) return 2;
	return 1;
}

/** The `name` table: strings tagged by platform, encoding, language and name ID. */
function parseNameTable(view: DataView): Map<number, string> {
	const out = new Map<number, string>();
	const ranks = new Map<number, number>();
	if (view.byteLength < 6) return out;

	const count = view.getUint16(2);
	const storage = view.getUint16(4);

	for (let i = 0; i < count; i++) {
		const record = 6 + i * 12;
		if (record + 12 > view.byteLength) break;

		const platformId = view.getUint16(record);
		const languageId = view.getUint16(record + 4);
		const nameId = view.getUint16(record + 6);
		const length = view.getUint16(record + 8);
		const offset = storage + view.getUint16(record + 10);
		if (offset + length > view.byteLength) continue;

		const rank = rankRecord(platformId, languageId);
		if (rank <= (ranks.get(nameId) ?? 0)) continue;

		const bytes = new Uint8Array(view.buffer, view.byteOffset + offset, length);
		const value =
			platformId === WINDOWS_PLATFORM
				? new TextDecoder('utf-16be').decode(bytes)
				: new TextDecoder('macintosh').decode(bytes);

		if (value.trim()) {
			out.set(nameId, value.trim());
			ranks.set(nameId, rank);
		}
	}

	return out;
}
